import type Geometry from 'ol/geom/Geometry';
import MultiPolygon from 'ol/geom/MultiPolygon';
import Polygon from 'ol/geom/Polygon';
import type { ClassificationScenario, LayerModel, OperationalAttributeField, ScenarioOverrides, ScenarioState, SelectionSet } from '../core/types';
import type { ClassificationConfig, ClassificationLevel } from '../presentation/config';
import { activeArea, masterClassificationAssignments, type ClassificationAssignments } from './classification';
import { entityViewEntities } from './entityView';
import type { MixedThematicPiece } from './mixedThematic';

/**
 * V1.9.0 classification scenarios: «Actual» is the classification as it is (Excel, project fields, Maestro); a
 * scenario («Propuesta») overlays proposed categories on some Suertes and Tablones. Nothing here writes a source.
 *
 * Resolution, per ACTIVE fragment: Tablón override → Suerte override → the entity's own «Actual» category. Tablones
 * partition the fragments of their Suerte, so a partly converted Suerte is split into pieces by grouping fragments,
 * without clipping polygons, and every area is a sum of active fragments: Actual and Propuesta always add up to the
 * same total.
 */

export const EMPTY_SCENARIOS: ScenarioState = { active: null, items: [] };
export const SCENARIO_NAME_MAX = 60;
export const SCENARIO_VALUE_MAX = 80;
const OVERRIDES_MAX = 20_000;
const EMPTY_OVERRIDES: ScenarioOverrides = { suerte: {}, tablon: {} };

const text = (value: unknown, max: number): string => typeof value === 'string' ? value.trim().slice(0, max) : '';
const record = (value: unknown): Record<string, string> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const entries = Object.entries(value as Record<string, unknown>).flatMap(([key, item]) => {
    const category = text(item, SCENARIO_VALUE_MAX);
    return typeof key === 'string' && key && category ? [[key, category] as const] : [];
  });
  return Object.fromEntries(entries.slice(0, OVERRIDES_MAX));
};

/** Stored scenarios (project file): validated; a project without them is «Actual» only. */
export function normalizeScenarioState(value: unknown): ScenarioState {
  if (!value || typeof value !== 'object') return EMPTY_SCENARIOS;
  const source = value as { active?: unknown; items?: unknown };
  const seen = new Set<string>();
  const items = (Array.isArray(source.items) ? source.items : []).slice(0, 50).flatMap((item): ClassificationScenario[] => {
    if (!item || typeof item !== 'object') return [];
    const raw = item as Record<string, unknown>;
    const id = text(raw.id, 120); const field = text(raw.field, 400);
    if (!id || !field || seen.has(id)) return [];
    seen.add(id);
    const overrides = raw.overrides && typeof raw.overrides === 'object' ? raw.overrides as Record<string, unknown> : {};
    const splitPart = raw.splitPart === 'primary' || raw.splitPart === 'secondary' ? raw.splitPart : undefined;
    const now = new Date(0).toISOString();
    return [{
      id, name: text(raw.name, SCENARIO_NAME_MAX) || 'Propuesta', field, ...(splitPart ? { splitPart } : {}),
      overrides: { suerte: record(overrides.suerte), tablon: record(overrides.tablon) },
      createdAt: text(raw.createdAt, 40) || now, updatedAt: text(raw.updatedAt, 40) || now,
    }];
  });
  const active = typeof source.active === 'string' && items.some(({ id }) => id === source.active) ? source.active : null;
  const mark = (source as { mark?: unknown }).mark;
  return { active, items, ...(mark === 'top' || mark === 'bottom' ? { mark } : {}) };
}

export const scenarioAssignmentCount = (scenario: ClassificationScenario): number =>
  Object.keys(scenario.overrides.suerte).length + Object.keys(scenario.overrides.tablon).length;

/** The scenario shown now, if any. */
export const shownScenario = (scenarios: ScenarioState): ClassificationScenario | null =>
  scenarios.active ? scenarios.items.find(({ id }) => id === scenarios.active) ?? null : null;

export interface ScenarioStatus { readonly applies: boolean; readonly reason?: string }

/** Whether a scenario can be drawn over a classification (same field and split part, categorized). */
export function scenarioStatus(scenario: ClassificationScenario | null, config: Pick<ClassificationConfig, 'field' | 'splitPart' | 'mode'>): ScenarioStatus {
  if (!scenario) return { applies: false };
  if (!config.field) return { applies: false, reason: 'Clasifica primero el mapa con el dato del escenario.' };
  if (config.mode !== 'categorized') return { applies: false, reason: 'Los escenarios trabajan sobre una clasificación por categorías.' };
  if (config.field !== scenario.field || (config.splitPart ?? null) !== (scenario.splitPart ?? null)) return { applies: false, reason: 'La clasificación activa usa otro dato: el escenario se aplica al dato con el que se creó.' };
  return { applies: true };
}

export function createScenario(scenarios: ScenarioState, name: string, config: Pick<ClassificationConfig, 'field' | 'splitPart'>, id: string, now: string): ScenarioState {
  if (!config.field) return scenarios;
  const scenario: ClassificationScenario = { id, name: text(name, SCENARIO_NAME_MAX) || 'Propuesta', field: config.field, ...(config.splitPart ? { splitPart: config.splitPart } : {}), overrides: EMPTY_OVERRIDES, createdAt: now, updatedAt: now };
  return { active: id, items: [...scenarios.items, scenario] };
}

export const replaceScenario = (scenarios: ScenarioState, scenario: ClassificationScenario): ScenarioState =>
  ({ ...scenarios, items: scenarios.items.map((item) => item.id === scenario.id ? scenario : item) });

export interface ScenarioTarget { readonly level: 'suerte' | 'tablon'; readonly id: string }

/**
 * Entities a selection assigns: Suertes and Tablones as selected (a Tablón is never widened to its Suerte); a Finca
 * means its Suertes. Fragments are not assignable on their own (they are reported as skipped).
 */
export function scenarioTargets(model: LayerModel, selection: SelectionSet): { readonly targets: readonly ScenarioTarget[]; readonly skipped: number } {
  const targets = new Map<string, ScenarioTarget>(); let skipped = 0;
  selection.entries.forEach((entry) => {
    if (entry.source !== 'master') { skipped += 1; return; }
    if (entry.kind === 'suerte' && model.suertes.has(entry.id)) targets.set(entry.id, { level: 'suerte', id: entry.id });
    else if (entry.kind === 'tablon' && model.tablones.has(entry.id)) targets.set(entry.id, { level: 'tablon', id: entry.id });
    else if (entry.kind === 'finca' && model.fincas.has(entry.id)) model.fincas.get(entry.id)!.suerteIds.forEach((id) => targets.set(id, { level: 'suerte', id }));
    else skipped += 1;
  });
  return { targets: [...targets.values()], skipped };
}

/**
 * Assign a proposed category. A whole Suerte replaces any Tablón assignment inside it (the user asked for the whole
 * Suerte); a Tablón keeps the rest of its Suerte as it is.
 */
export function assignScenario(scenario: ClassificationScenario, model: LayerModel, targets: readonly ScenarioTarget[], value: string, now: string): ClassificationScenario {
  const category = text(value, SCENARIO_VALUE_MAX); if (!category || !targets.length) return scenario;
  const suerte = { ...scenario.overrides.suerte }; const tablon = { ...scenario.overrides.tablon };
  targets.forEach((target) => {
    if (target.level === 'tablon') { tablon[target.id] = category; return; }
    suerte[target.id] = category;
    model.suertes.get(target.id)?.tablonIds.forEach((id) => { delete tablon[id]; });
  });
  return { ...scenario, overrides: { suerte, tablon }, updatedAt: now };
}

/** Remove assignments: a Suerte also clears its Tablones; a Tablón only itself. The «Actual» value comes back. */
export function clearScenarioTargets(scenario: ClassificationScenario, model: LayerModel, targets: readonly ScenarioTarget[], now: string): ClassificationScenario {
  const suerte = { ...scenario.overrides.suerte }; const tablon = { ...scenario.overrides.tablon };
  targets.forEach((target) => {
    if (target.level === 'tablon') { delete tablon[target.id]; return; }
    delete suerte[target.id];
    model.suertes.get(target.id)?.tablonIds.forEach((id) => { delete tablon[id]; });
  });
  return { ...scenario, overrides: { suerte, tablon }, updatedAt: now };
}

/** Proposed category of one fragment, or undefined when the scenario does not change it. */
export function overrideForFragment(scenario: ClassificationScenario, model: LayerModel, fragmentId: string): string | undefined {
  const fragment = model.fragmentsById.get(fragmentId); if (!fragment) return undefined;
  return scenario.overrides.tablon[fragment.tablonId] ?? scenario.overrides.suerte[fragment.suerteId];
}

export interface ScenarioFlow {
  /** Entity of the classification level (the row of «Ver por»). */
  readonly entityId: string;
  /** «Actual» category of this part of the entity. */
  readonly from: string;
  /** Category of the same part in the scenario (equal to `from` when unchanged). */
  readonly to: string;
  readonly areaHa: number;
}

export interface ScenarioAssignments extends ClassificationAssignments {
  /** Entities whose active surface ends up in more than one category (keyed by entity id at the classification level). */
  readonly piecesByEntityId: ReadonlyMap<string, readonly MixedThematicPiece[]>;
  /** «Actual» category per fragment (the base category of its entity). */
  readonly actualByFragmentId: ReadonlyMap<string, string>;
  /**
   * The whole classified active surface, part by part: Actual → Propuesta. Areas, counts, indicators and the Inspector
   * all read these parts, so the Actual side always equals the «Actual» map (Áreas parciales included) and both sides
   * add up to the same total.
   */
  readonly flows: readonly ScenarioFlow[];
}

const polygonsOf = (geometry: Geometry | undefined): Polygon[] =>
  geometry instanceof Polygon ? [geometry] : geometry instanceof MultiPolygon ? geometry.getPolygons() : [];
/** Drawing geometry of a group of fragments (no clipping: Tablones and Suertes are unions of fragments). */
export const fragmentsGeometry = (model: LayerModel, fragmentIds: readonly string[]): MultiPolygon =>
  new MultiPolygon(fragmentIds.flatMap((id) => polygonsOf(model.fragmentsById.get(id)?.feature.getGeometry())));

/** Mutable result while rows are composed (shared by the plain and the Áreas parciales compositions). */
export interface ScenarioAccumulator {
  readonly categoryByEntityId: Map<string, string>; readonly categoryByFragmentId: Map<string, string>;
  readonly hasValueByEntityId: Map<string, boolean>; readonly hasValueByFragmentId: Map<string, boolean>;
  readonly counts: Map<string, number>; readonly areaHa: Map<string, number>;
  readonly piecesByEntityId: Map<string, readonly MixedThematicPiece[]>; readonly flows: ScenarioFlow[];
}

export const scenarioAccumulator = (): ScenarioAccumulator => ({
  categoryByEntityId: new Map(), categoryByFragmentId: new Map(), hasValueByEntityId: new Map(), hasValueByFragmentId: new Map(),
  counts: new Map(), areaHa: new Map(), piecesByEntityId: new Map(), flows: [],
});

/**
 * Closes one entity: its parts (flows) give the areas, the count in every category it shows and its main category
 * (the largest, unless `main` is given); it is drawn in pieces only when it shows more than one.
 */
export function finishScenarioRow(acc: ScenarioAccumulator, entityId: string, flows: readonly ScenarioFlow[], pieces: readonly MixedThematicPiece[], main?: string): void {
  const byCategory = new Map<string, number>();
  flows.forEach((flow) => { byCategory.set(flow.to, (byCategory.get(flow.to) ?? 0) + flow.areaHa); acc.flows.push(flow); });
  byCategory.forEach((area, category) => { acc.areaHa.set(category, (acc.areaHa.get(category) ?? 0) + area); acc.counts.set(category, (acc.counts.get(category) ?? 0) + 1); });
  const largest = [...byCategory].reduce<readonly [string, number] | null>((best, item) => !best || item[1] > best[1] ? item : best, null);
  const category = main ?? largest?.[0]; if (category !== undefined) acc.categoryByEntityId.set(entityId, category);
  if (pieces.length > 1) acc.piecesByEntityId.set(entityId, pieces);
}

/**
 * One entity without Áreas parciales: Tablón override → Suerte override → its «Actual» category, per active fragment;
 * the fragments are grouped by resulting category (no clipping).
 */
export function addScenarioRow(acc: ScenarioAccumulator, model: LayerModel, row: { readonly id: string; readonly fragmentIds: readonly string[] }, base: string, baseHasValue: boolean, scenario: ClassificationScenario): void {
  const groups = new Map<string, { fragments: string[]; proposed: boolean }>();
  row.fragmentIds.forEach((id) => {
    const proposed = overrideForFragment(scenario, model, id); const category = proposed ?? base;
    const group = groups.get(category) ?? { fragments: [], proposed: false };
    group.fragments.push(id); group.proposed ||= proposed !== undefined; groups.set(category, group);
    acc.categoryByFragmentId.set(id, category); acc.hasValueByFragmentId.set(id, proposed !== undefined || baseHasValue);
  });
  const parts = [...groups].map(([category, group]) => ({ category, group, area: activeArea(model, group.fragments) }));
  acc.hasValueByEntityId.set(row.id, parts.some(({ group }) => group.proposed) || baseHasValue);
  const pieces = parts.length > 1 ? parts.map(({ category, group, area }): MixedThematicPiece => ({
    parentSuerteId: row.id, source: group.proposed ? 'scenario' : 'base', sourceId: row.id, category,
    hasValue: group.proposed || baseHasValue, areaHa: area, geometry: fragmentsGeometry(model, group.fragments),
  })) : [];
  finishScenarioRow(acc, row.id, parts.map(({ category, area }) => ({ entityId: row.id, from: base, to: category, areaHa: area })), pieces);
}

let memo: { key: readonly unknown[]; result: ScenarioAssignments } | null = null;

/**
 * The principal classification with a scenario applied, at any «Ver por» (`level`; the stored `config.level` is not
 * read, so callers pass the configuration object itself and the memo holds across map, legend, panel and PNG). Same rows, same «Actual» categories and the
 * same area method as `masterClassificationAssignments`; only overridden fragments change. Counts: an entity counts
 * once in each category it holds (a converted Tablón makes its Suerte appear in two categories); areas never repeat.
 * With Áreas parciales in effect the composition is `scenarioPartialAssignments` (domain/scenarioPartials).
 */
export function scenarioClassificationAssignments(model: LayerModel, config: ClassificationConfig, level: ClassificationLevel, scoped: ReadonlySet<string>, fields: readonly OperationalAttributeField[], scenario: ClassificationScenario): ScenarioAssignments {
  const key = [model, config, level, scoped, fields, scenario];
  if (memo && memo.key.every((value, index) => value === key[index])) return memo.result;
  const actual = masterClassificationAssignments(model, config.field!, level, scoped, fields, { ...config, level });
  const acc = scenarioAccumulator();
  entityViewEntities(model, level === 'feature' ? 'fragment' : level, scoped).forEach((row) => {
    addScenarioRow(acc, model, row, actual.categoryByEntityId.get(row.id) ?? 'Sin valor', actual.hasValueByEntityId.get(row.id) ?? false, scenario);
  });
  const result: ScenarioAssignments = { ...acc, actualByFragmentId: actual.categoryByFragmentId };
  memo = { key, result };
  return result;
}

export interface ScenarioTransfer { readonly from: string; readonly to: string; readonly areaHa: number; readonly entities: number }
export interface ScenarioCategoryBalance { readonly category: string; readonly actualHa: number; readonly proposedHa: number }
export interface ScenarioIndicators {
  readonly totalHa: number;
  readonly balances: readonly ScenarioCategoryBalance[];
  /** Area moving from one category to another (only the changed parts). */
  readonly transfers: readonly ScenarioTransfer[];
  /** Assignments whose entity has no active fragment in the model (e.g. excluded by a geometry edit): 0 ha. */
  readonly withoutArea: number;
}

const indicatorsCache = new WeakMap<ScenarioAssignments, ScenarioIndicators>();

/**
 * Actual vs Propuesta, from the same parts as the map (the totals are equal by construction). Memoized per
 * assignments object (itself memoized per model, classification, level, visible set and scenario).
 */
export function scenarioIndicators(model: LayerModel, assignments: ScenarioAssignments, scenario: ClassificationScenario): ScenarioIndicators {
  const cached = indicatorsCache.get(assignments); if (cached) return cached;
  const result = computeIndicators(model, assignments, scenario);
  indicatorsCache.set(assignments, result);
  return result;
}

function computeIndicators(model: LayerModel, assignments: ScenarioAssignments, scenario: ClassificationScenario): ScenarioIndicators {
  const actual = new Map<string, number>(); const proposed = new Map<string, number>();
  const transfers = new Map<string, { from: string; to: string; areaHa: number; entities: Set<string> }>();
  let totalHa = 0;
  assignments.flows.forEach(({ entityId, from, to, areaHa }) => {
    totalHa += areaHa;
    actual.set(from, (actual.get(from) ?? 0) + areaHa); proposed.set(to, (proposed.get(to) ?? 0) + areaHa);
    if (from === to) return;
    const key = `${from}\u0000${to}`; const item = transfers.get(key) ?? { from, to, areaHa: 0, entities: new Set<string>() };
    item.areaHa += areaHa; item.entities.add(entityId); transfers.set(key, item);
  });
  const categories = [...new Set([...actual.keys(), ...proposed.keys()])].sort((a, b) => a.localeCompare(b, 'es', { numeric: true }));
  const withoutArea = [...Object.keys(scenario.overrides.suerte).map((id) => model.suertes.get(id)?.fragmentIds.length ?? 0), ...Object.keys(scenario.overrides.tablon).map((id) => model.tablones.get(id)?.fragmentIds.length ?? 0)].filter((count) => count === 0).length;
  return {
    totalHa,
    balances: categories.map((category) => ({ category, actualHa: actual.get(category) ?? 0, proposedHa: proposed.get(category) ?? 0 })),
    transfers: [...transfers.values()].map(({ from, to, areaHa, entities }) => ({ from, to, areaHa, entities: entities.size })).sort((a, b) => b.areaHa - a.areaHa),
    withoutArea,
  };
}
